/**
 * `GET /v1/transactions` — what has moved, newest first (#140).
 *
 * The backend's own description names this repo as the reason the route exists:
 * *"Until it existed, the web app's 'recent activity' and 'recent payments'
 * were a fixtures file rendered to real customers as their own history."*
 * (`go-wallet#973`.) This module is that fixtures file's replacement.
 *
 * # Scoped by shape, not by a check
 *
 * There is no parameter on the route that can name a customer. "A history is
 * only readable by the person who lived it" is therefore a property of the
 * shape rather than of an authorisation this app could get wrong — and this
 * module adds no parameter of its own.
 *
 * # What a naive read gets wrong
 *
 *   - **`state` is recorded, never inferred from a date.** A settled item and a
 *     pending one can share a value date.
 *   - **`settled` is absent until the institution has spoken.** Rendering zero
 *     would claim it settled nothing when it has not yet said anything.
 *   - **`counterparty` is absent rather than blank** when unknown. An empty
 *     string reads as a counterparty called nothing.
 *   - **`message.display` gates the text.** Anonymous stranger-supplied text
 *     landing unfiltered in a creator's transaction list is the gap the seam
 *     closes (FR-22.3); a client that reads `text` without checking `display`
 *     hands that straight back.
 *   - **Page until `next_before` is ABSENT**, not until a page is short. A
 *     short page is not the end.
 */
import { moneyFromDTO } from '@/lib/money'
import type { Money } from '@/lib/money'
import type { WalletApi } from '@/lib/api'

export const DIRECTIONS = ['in', 'out'] as const
export type Direction = (typeof DIRECTIONS)[number]

/**
 * What the seam decided about the sender's text.
 *
 * `display` is the field to branch on. `text` is present only when the seam
 * says it is readable, and `notice` is what to say instead when it is not.
 */
export interface TransactionMessage {
  readonly display: string
  readonly text?: string
  readonly notice?: string
}

export interface Transaction {
  /**
   * The row's identity and its cursor, so a customer quoting "the payment at
   * the top of my list" names something support can resolve. Opaque; never
   * parsed and never shown as though it meant something.
   */
  readonly ref: string
  readonly direction: Direction
  /**
   * Pending, settled or reversed AS RECORDED.
   *
   * Deliberately a bare string rather than a closed union: nothing here
   * branches on an unrecognised value beyond labelling it, and refusing the
   * whole page because the backend added a fourth state would cost the customer
   * their history to protect a label. The same call `decodeAccount` makes about
   * a currency, and the opposite of the one `balances.ts` makes about a scale.
   */
  readonly state: string
  /** What was authorised. */
  readonly amount: Money
  /** Present only once the institution has settled it. */
  readonly settled?: Money
  /** The institution's date — what a statement is booked on. */
  readonly valueDate: string
  /** The name as recorded. Absent, never blank. */
  readonly counterparty?: string
  readonly message?: TransactionMessage
}

export type TransactionsState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready'
      readonly transactions: readonly Transaction[]
      /** Absent on the last page. Its ABSENCE is the end, not a short page. */
      readonly nextBefore?: string
    }
  /** The customer has no history yet. NOT the same as being unable to read one. */
  | { readonly status: 'none' }
  | { readonly status: 'unavailable'; readonly reason: string }

interface TransactionDTO {
  ref?: unknown
  direction?: unknown
  state?: unknown
  amount?: unknown
  settled?: unknown
  value_date?: unknown
  counterparty?: unknown
  message?: { display?: unknown; text?: unknown; notice?: unknown }
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

function isDirection(v: unknown): v is Direction {
  return typeof v === 'string' && (DIRECTIONS as readonly string[]).includes(v)
}

export function decodeTransaction(dto: TransactionDTO): Transaction | string {
  const ref = str(dto.ref)
  if (ref === undefined) return 'a transaction arrived with no reference'

  if (!isDirection(dto.direction)) {
    return `transaction ${ref} arrived with no usable direction (${String(dto.direction)})`
  }

  const state = str(dto.state)
  if (state === undefined) return `transaction ${ref} arrived with no state`

  const valueDate = str(dto.value_date)
  if (valueDate === undefined) return `transaction ${ref} arrived with no value date`

  let amount: Money
  try {
    amount = moneyFromDTO(dto.amount as never)
  } catch (err) {
    return `transaction ${ref} carries an amount that cannot be read (${err instanceof Error ? err.message : String(err)})`
  }

  // Absent is a fact, not a failure: the institution has not settled it yet.
  // Present-but-unreadable IS a failure, and is not quietly dropped — a row
  // that silently loses its settled figure looks identical to a pending one.
  let settled: Money | undefined
  if (dto.settled !== undefined && dto.settled !== null) {
    try {
      settled = moneyFromDTO(dto.settled as never)
    } catch (err) {
      return `transaction ${ref} carries a settled figure that cannot be read (${err instanceof Error ? err.message : String(err)})`
    }
  }

  const counterparty = str(dto.counterparty)

  let message: TransactionMessage | undefined
  const m = dto.message
  if (m !== undefined && m !== null) {
    const display = str(m.display)
    // `display` is the contract's only required field on the object, and it is
    // what decides whether the text may be shown. A message object without it
    // cannot be rendered safely in either direction, so the row is refused
    // rather than shown with the text guessed at.
    if (display === undefined) return `transaction ${ref} carries a message with no display decision`
    const text = str(m.text)
    const notice = str(m.notice)
    message = { display, ...(text !== undefined ? { text } : {}), ...(notice !== undefined ? { notice } : {}) }
  }

  return {
    ref,
    direction: dto.direction,
    state,
    amount,
    ...(settled ? { settled } : {}),
    valueDate,
    ...(counterparty ? { counterparty } : {}),
    ...(message ? { message } : {}),
  }
}

export interface TransactionsQuery {
  readonly limit?: number
  readonly before?: string
}

export async function fetchTransactions(
  api: WalletApi,
  query: TransactionsQuery = {},
): Promise<TransactionsState> {
  const params = new URLSearchParams()
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.before !== undefined) params.set('before', query.before)
  const qs = params.toString()

  let dto: { transactions?: unknown; next_before?: unknown }
  try {
    dto = await api.get<{ transactions?: unknown; next_before?: unknown }>(
      `/v1/transactions${qs ? `?${qs}` : ''}`,
    )
  } catch (err) {
    return { status: 'unavailable', reason: err instanceof Error ? err.message : String(err) }
  }

  if (!Array.isArray(dto?.transactions)) {
    return { status: 'unavailable', reason: 'the backend did not return a list of transactions' }
  }

  const transactions: Transaction[] = []
  for (const raw of dto.transactions as TransactionDTO[]) {
    const decoded = decodeTransaction(raw ?? {})
    // One unreadable row fails the page rather than silently shortening it. A
    // history missing a payment its owner remembers is worse than one that says
    // it could not be read — the same call `fetchAccounts` makes, for the same
    // reason: a customer cannot see what they were not shown.
    if (typeof decoded === 'string') return { status: 'unavailable', reason: decoded }
    transactions.push(decoded)
  }

  if (transactions.length === 0 && query.before === undefined) return { status: 'none' }

  const nextBefore = str(dto.next_before)
  return { status: 'ready', transactions, ...(nextBefore ? { nextBefore } : {}) }
}
